/**
 * Share the booth's location: watch GPS (high accuracy) for a few seconds, show ± metres while it
 * settles, keep the best fix, warn above 25 m but still allow sending (DECISIONS.md #6). Sending
 * goes through the outbox, so it works offline; capturedAt is the time of the fix.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ACCURACY_WARN_M, betterFix, isAccurate, mapsUrl, shouldStopWatching, type Fix } from "@core/location";
import { outboxErrorText } from "@core/messages";
import { formatTime } from "../i18n";
import { watchGps, type GeoError, type GeoWatch } from "../platform/browser";
import { useApp } from "../ui/context";
import { navigate } from "../ui/router";
import { Banner, Button } from "../ui/components";

type Phase = "watching" | "done" | "error";

export function LocationScreen({ code }: { code: string }) {
  const { app, t, lang } = useApp();
  const [phase, setPhase] = useState<Phase>("watching");
  const [best, setBest] = useState<Fix | null>(null);
  const [latest, setLatest] = useState<Fix | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [geoError, setGeoError] = useState<GeoError | null>(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [editable, setEditable] = useState(true);

  const watch = useRef<GeoWatch | null>(null);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);
  const bestRef = useRef<Fix | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    void app.booth(code).then((b) => setEditable(!!b && app.canEdit(b)));
  }, [app, code]);

  const stop = useCallback(() => {
    watch.current?.stop();
    watch.current = null;
    if (tick.current) clearInterval(tick.current);
    tick.current = null;
  }, []);

  const start = useCallback(() => {
    stop();
    setResult(null);
    setGeoError(null);
    setBest(null);
    setLatest(null);
    bestRef.current = null;
    setElapsed(0);
    setPhase("watching");
    const startedAt = Date.now();
    tick.current = setInterval(() => {
      const now = Date.now();
      setElapsed(Math.floor((now - startedAt) / 1000));
      if (shouldStopWatching(bestRef.current, startedAt, now)) {
        stop();
        setPhase("done");
      }
    }, 500);
    watch.current = watchGps(
      (fix) => {
        setLatest(fix);
        bestRef.current = betterFix(bestRef.current, fix);
        setBest(bestRef.current);
      },
      (e) => {
        if (e === "timeout" && bestRef.current) return;
        stop();
        setGeoError(e);
        setPhase("error");
      }
    );
  }, [stop]);

  useEffect(() => {
    start();
    return () => {
      stop();
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [start, stop]);

  const send = async () => {
    const fix = bestRef.current;
    if (!fix) return;
    stop();
    setPhase("done");
    setSending(true);
    try {
      const r = await app.core.shareLocation(code, fix);
      if (!r.ok) {
        setResult({ kind: "error", text: r.reason === "outside_india" ? t.locOutsideIndia : t.locNoFix });
        return;
      }
      const items = app.assemblyId ? await app.core.store.outboxForBooth(app.assemblyId, code) : [];
      const failed = items.filter((i) => i.kind === "location" && i.status === "failed").at(-1);
      if (failed?.lastError) setResult({ kind: "error", text: `${t.locationFailed}: ${outboxErrorText(failed.lastError, lang)}` });
      else {
        setResult({ kind: "ok", text: r.synced ? t.locSentSynced : t.locSentQueued });
        closeTimer.current = setTimeout(() => navigate({ name: "booth", code }), 1500);
      }
    } catch (e) {
      setResult({ kind: "error", text: `${t.err_generic}: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setSending(false);
    }
  };

  const acc = best?.accuracyM;
  const good = isAccurate(best);
  if (!editable) return <Banner kind="warn">{t.notAllowedEdit}</Banner>;

  return (
    <div data-testid="location-screen">
      <h2>
        {t.boothNo} {code} — {t.locTitle}
      </h2>
      <p>{t.locIntro}</p>

      {geoError ? (
        <Banner kind="error" testId="geo-error">
          {geoError === "insecure" ? t.locNeedsHttps : geoError === "denied" ? t.locPermissionWeb : geoError === "unavailable" ? t.locUnavailable : t.locNoFix}
        </Banner>
      ) : null}

      {!geoError ? (
        <div className={`gauge${best ? (good ? " good" : " weak") : ""}`} aria-live="polite" data-testid="gauge">
          <div style={{ fontWeight: 800 }}>{t.locAccuracy}</div>
          <div className="value" data-testid="accuracy">
            {acc != null ? `±${Math.round(acc)} ${t.metres}` : "…"}
          </div>
          <div style={{ fontWeight: 700 }}>
            {phase === "watching" ? `${t.locSearching} ${t.locWatchingFor(elapsed)}` : best && good ? `✓ ${t.locGood}` : ""}
          </div>
          {latest && latest !== best && latest.accuracyM != null ? (
            <div className="muted">
              ({Math.round(latest.accuracyM)} {t.metres})
            </div>
          ) : null}
          {best ? (
            <div className="coords">
              {best.lat.toFixed(6)}, {best.lng.toFixed(6)}
              <br />
              {t.locCapturedAt(formatTime(best.timestamp, lang))}
            </div>
          ) : null}
        </div>
      ) : null}

      {best && !good ? <Banner kind="warn">{t.locWeak(ACCURACY_WARN_M)}</Banner> : null}
      {result ? (
        <Banner kind={result.kind} testId="location-result">
          {result.text}
        </Banner>
      ) : null}

      <div className="stack">
        {best && !result ? (
          <Button big busy={sending} onClick={send} sub={acc != null ? `±${Math.round(acc)} ${t.metres}` : undefined} testId="location-send">
            📍 {good ? t.locSend : t.locSendAnyway}
          </Button>
        ) : null}
        {phase === "done" && !best && !result ? <Banner kind="warn">{t.locNoFix}</Banner> : null}
        {phase !== "watching" || result ? (
          <Button variant="secondary" onClick={start}>
            ↻ {t.locTryAgain}
          </Button>
        ) : null}
        {best ? (
          <a className="btn btn-ghost" href={mapsUrl(best.lat, best.lng)} target="_blank" rel="noreferrer">
            {t.openInMaps}
          </a>
        ) : null}
      </div>
    </div>
  );
}
